import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../hooks/useLanguage';
import axios from 'axios';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  BarChart3, TrendingUp, Clock, ShoppingCart, DollarSign,
  Package, Flame, Trophy, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, LineChart, Line
} from 'recharts';

const COLORS = ['#D97706', '#2563EB', '#10B981', '#EF4444', '#7C3AED', '#EC4899', '#0891B2', '#F59E0B'];

export default function ReportsPage() {
  const { API, authHeaders, activeBranch } = useAuth();
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const l = (ar, en) => isAr ? ar : en;

  const [days, setDays] = useState('30');
  const [overview, setOverview] = useState(null);
  const [topItems, setTopItems] = useState([]);
  const [peakHours, setPeakHours] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dailyTrend, setDailyTrend] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReports = useCallback(async () => {
    if (!activeBranch?.id) return;
    setLoading(true);
    const headers = authHeaders();
    const base = `${API}/reports`;
    const q = `branch_id=${activeBranch.id}&days=${days}`;
    try {
      const [ov, ti, ph, cat, dt] = await Promise.all([
        axios.get(`${base}/overview?${q}`, headers),
        axios.get(`${base}/top-items?${q}`, headers),
        axios.get(`${base}/peak-hours?${q}`, headers),
        axios.get(`${base}/sales-by-category?${q}`, headers),
        axios.get(`${base}/daily-trend?${q}`, headers),
      ]);
      setOverview(ov.data);
      setTopItems(ti.data.top_items || []);
      setPeakHours(ph.data.peak_hours || []);
      setCategories(cat.data.categories || []);
      setDailyTrend(dt.data.daily_trend || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [API, authHeaders, activeBranch, days]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const statCards = overview ? [
    { label: l('إجمالي الإيرادات', 'Total Revenue'), value: `${overview.total_revenue.toLocaleString()} ${l('ر.س', 'SAR')}`, icon: DollarSign, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
    { label: l('إجمالي الطلبات', 'Total Orders'), value: overview.total_orders, icon: ShoppingCart, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { label: l('متوسط قيمة الطلب', 'Avg Order Value'), value: `${overview.avg_order_value} ${l('ر.س', 'SAR')}`, icon: TrendingUp, color: 'text-amber-500', bg: 'bg-amber-500/10' },
    { label: l('نسبة التوصيل', 'Delivery Rate'), value: `${overview.delivery_rate}%`, icon: Package, color: 'text-purple-500', bg: 'bg-purple-500/10' },
  ] : [];

  return (
    <ModuleLayout>
      <div className="space-y-6" data-testid="reports-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Cairo, sans-serif' }}>
              <BarChart3 className="w-6 h-6 inline-block me-2 text-amber-500" />
              {l('التقارير المتقدمة', 'Advanced Reports')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{l('تحليلات شاملة لأداء مطعمك', 'Comprehensive analytics for your restaurant')}</p>
          </div>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-36 h-9" data-testid="reports-period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">{l('آخر 7 أيام', 'Last 7 days')}</SelectItem>
              <SelectItem value="30">{l('آخر 30 يوم', 'Last 30 days')}</SelectItem>
              <SelectItem value="90">{l('آخر 90 يوم', 'Last 90 days')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Stat Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {statCards.map((s, i) => (
                <Card key={i} data-testid={`stat-card-${i}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.bg}`}>
                        <s.icon className={`w-5 h-5 ${s.color}`} />
                      </div>
                      <div>
                        <p className="text-[11px] text-muted-foreground">{s.label}</p>
                        <p className="text-lg font-bold text-foreground">{s.value}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Daily Trend */}
              <Card data-testid="daily-trend-chart">
                <CardContent className="p-4">
                  <h3 className="font-bold text-sm mb-3" style={{ fontFamily: 'Cairo, sans-serif' }}>
                    <TrendingUp className="w-4 h-4 inline-block me-1.5 text-amber-500" />
                    {l('اتجاه المبيعات اليومي', 'Daily Sales Trend')}
                  </h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={dailyTrend}>
                        <defs>
                          <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#D97706" stopOpacity={0.3} />
                            <stop offset="100%" stopColor="#D97706" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip
                          contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: 12 }}
                          formatter={(v) => [`${v.toFixed(0)} ${l('ر.س', 'SAR')}`, l('الإيرادات', 'Revenue')]}
                        />
                        <Area type="monotone" dataKey="revenue" stroke="#D97706" fill="url(#revenueGrad)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Peak Hours */}
              <Card data-testid="peak-hours-chart">
                <CardContent className="p-4">
                  <h3 className="font-bold text-sm mb-3" style={{ fontFamily: 'Cairo, sans-serif' }}>
                    <Clock className="w-4 h-4 inline-block me-1.5 text-blue-500" />
                    {l('ساعات الذروة', 'Peak Hours')}
                  </h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={peakHours}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={2} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: 12 }} />
                        <Bar dataKey="orders" fill="#2563EB" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Top Selling Items */}
              <Card data-testid="top-items-chart">
                <CardContent className="p-4">
                  <h3 className="font-bold text-sm mb-3" style={{ fontFamily: 'Cairo, sans-serif' }}>
                    <Trophy className="w-4 h-4 inline-block me-1.5 text-amber-500" />
                    {l('الأصناف الأكثر مبيعاً', 'Top Selling Items')}
                  </h3>
                  {topItems.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-8 text-center">{l('لا توجد بيانات', 'No data')}</p>
                  ) : (
                    <div className="space-y-2.5">
                      {topItems.slice(0, 8).map((item, i) => {
                        const maxQty = topItems[0]?.quantity || 1;
                        return (
                          <div key={i} className="flex items-center gap-3" data-testid={`top-item-${i}`}>
                            <span className="w-5 text-xs font-bold text-muted-foreground text-center">#{i + 1}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-xs font-semibold truncate">{item.name}</span>
                                <span className="text-xs text-muted-foreground">{item.quantity} {l('طلب', 'orders')}</span>
                              </div>
                              <div className="h-2 rounded-full bg-muted overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${(item.quantity / maxQty) * 100}%`, background: COLORS[i % COLORS.length] }} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Sales by Category */}
              <Card data-testid="category-chart">
                <CardContent className="p-4">
                  <h3 className="font-bold text-sm mb-3" style={{ fontFamily: 'Cairo, sans-serif' }}>
                    <Flame className="w-4 h-4 inline-block me-1.5 text-orange-500" />
                    {l('المبيعات حسب التصنيف', 'Sales by Category')}
                  </h3>
                  {categories.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-8 text-center">{l('لا توجد بيانات', 'No data')}</p>
                  ) : (
                    <div className="h-64 flex items-center justify-center">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={categories} dataKey="revenue" nameKey="category" cx="50%" cy="50%" outerRadius={90}
                            label={({ category, percent }) => `${category} (${(percent * 100).toFixed(0)}%)`}
                            labelLine={{ stroke: 'hsl(var(--muted-foreground))' }}>
                            {categories.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: 12 }}
                            formatter={(v) => [`${v.toFixed(0)} ${l('ر.س', 'SAR')}`, l('الإيرادات', 'Revenue')]} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </ModuleLayout>
  );
}
